import makeStyles from '@mui/styles/makeStyles';
import * as Sentry from '@sentry/react';
import qs from 'query-string';
import { FC, FormEvent, MouseEvent, useCallback, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { ConnectedProps, connect } from 'react-redux';

import { notify } from '@/actions/ui';
import { authenticateOtp, createOtpAuthConfiguration, otpQrCodeBase64 } from '@/auth/store';
import Button from '@/components/Button';
import Dialog, { DialogProps } from '@/components/Dialog';
import FaqLink from '@/components/FaqLink';
import { PhoneInput, RadioGroup, TextField } from '@/components/FormAdapters';
import Link from '@/components/Link';
import { EmailDeliveryHelp } from '@/componentsv2/EmailDeliveryHelp';
import config from '@/config';
import { APIError, OTP_METHODS, OtpMethod, apiErrorMessage } from '@/core/api';
import { RootState } from '@/store';
import { fetchUser } from '@/store/user';
import { darkGreen } from '@/theme/colors';
import { capitalize } from '@/utils';

const SETUP_ERROR = 'An error occurred when trying to set up two-factor authentication.';

// FB-96: no migrated 2FA article exists on the new help center yet, so this falls back
// to the account-management category (flagged for Julian). Point it at the dedicated
// article once one is published.
export const FAQ_URL = `${config.helpCenterUrl}/help-center/category/account-management/`;

const useStyles = makeStyles({
  qrCode: {
    width: 200,
    height: 200,
    margin: '0 auto',
  },
  title: {
    margin: '0 0 15px',
  },
  titleSuccess: {
    margin: 0,
    color: darkGreen,
  },
  paper: {
    fontSize: 14,
    textAlign: 'center',
    maxWidth: 410,
    boxSizing: 'border-box',
  },
  verificationCode: {
    maxWidth: 200,
    '& input': {
      fontSize: 20,
      textAlign: 'center',

      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
      },
    },
  },
  resend: {
    fontSize: 14,
    color: darkGreen,
    textTransform: 'none',
    fontWeight: 400,
    margin: '30px 0 0 5px',
    padding: '8px 11px',
  },
  text: {
    lineHeight: 1.5,
  },
});

interface User {
  id: string;
  username: string;
}

interface SuccessStepProps extends DialogProps {
  user?: User;
}

const SuccessStep: FC<SuccessStepProps> = ({ onClose, user, ...other }) => {
  const s = useStyles();
  return (
    <Dialog fullWidth classes={{ paper: s.paper }} onClose={onClose} {...other}>
      <h4 className={s.titleSuccess}>All set!</h4>
      <p className={s.text}>
        Successfully set up two-factor authentication.
        {`${user ? '' : ' A new verification code may be required for login.'}`}
      </p>
      <Button
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          if (onClose) onClose(e, 'escapeKeyDown');
        }}
        fullWidth={false}
      >
        Done
      </Button>
    </Dialog>
  );
};

const SecurityDialog = (props: DialogProps) => {
  const s = useStyles();
  return (
    <Dialog
      fullWidth
      cancelLabel="Cancel"
      confirmLabel="Continue"
      cancelButtonProps={{ size: 'sm' }}
      confirmButtonProps={{ size: 'sm' }}
      classes={{ paper: s.paper }}
      {...props}
    />
  );
};

interface AuthenticatorStepProps {
  otpAuthUri: string;
  qrCodeBase64: string;
  showFAQ?: boolean;
}

const AuthenticatorStep: FC<AuthenticatorStepProps> = ({
  otpAuthUri,
  qrCodeBase64,
  showFAQ = false,
}) => {
  const s = useStyles();

  const secret = useMemo(() => qs.parse(otpAuthUri).secret, [otpAuthUri]);

  return (
    <>
      <h4 className={s.title}>Set up two-factor authentication</h4>
      <p className={s.text}>
        Scan the QR code below with an authentication app, such as Google Authenticator, on your
        phone.
      </p>
      <Link to={otpAuthUri}>
        <img className={s.qrCode} src={qrCodeBase64} alt="Two factor authentication key" />
      </Link>
      <p className={s.text}>
        If you are unable to see the code, type this key on your authentication app (time based
        password):&nbsp;
        <Link to={otpAuthUri}>
          <b>{secret}</b>
        </Link>
      </p>
      <Field
        component={TextField}
        changeOnBlur={false}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        name="otp"
        label="Verification Code"
        variant="outlined"
        classes={{ root: s.verificationCode }}
        inputType="number"
      />
      {showFAQ && <FaqLink url={FAQ_URL} className={s.text} />}
    </>
  );
};

interface VerificationStepProps {
  address: string;
  method: string;
  user?: User;
  onSuccess: () => void;
  setOtp: (otp: string) => void;
  refetch?: () => Promise<void>;
  open: boolean;
  onCancel: () => void;
}

const verificationStepConnector = connect(undefined, {
  createOtpAuthConfiguration,
  authenticateOtp,
  notify,
  fetchUser,
});

interface FormData {
  otp: string;
}

type FormErrors = Partial<Record<keyof FormData, string>>;

function validate(values: FormData): FormErrors {
  const errors: FormErrors = {};

  if (!(values.otp || '').trim()) {
    errors.otp = 'Required';
  }
  return errors;
}

const VerificationStepBare: FC<
  VerificationStepProps & ConnectedProps<typeof verificationStepConnector>
> = ({
  address,
  method,
  createOtpAuthConfiguration,
  authenticateOtp,
  notify,
  onSuccess,
  user,
  setOtp,
  refetch,
  open,
  onCancel,
}) => {
  const s = useStyles();

  const handleSubmit = useCallback(
    async (values: FormData) => {
      try {
        const success = await authenticateOtp(values.otp);

        if (!success) {
          return { otp: 'Invalid verification code' };
        }

        if (!user) {
          await setOtp(values.otp);
        }

        if (refetch) refetch();
        onSuccess();
      } catch (err) {
        notify('An error occurred when trying to setup two-factor authentication.', 'error');
        throw err;
      }
    },
    [refetch, authenticateOtp, notify, onSuccess, setOtp, user]
  );

  const handleResend = useCallback(async () => {
    try {
      await createOtpAuthConfiguration(method, address);
      notify(`Verification code sent to ${address}`);
    } catch (err) {
      // A click handler drops the promise, so rethrowing only made an unhandled
      // rejection. Report it here instead (ENG-5322).
      Sentry.captureException(err);
      notify(apiErrorMessage(err, 'An error occurred when trying to setup otp.'), 'error');
    }
  }, [address, createOtpAuthConfiguration, method, notify]);

  return (
    <Form onSubmit={handleSubmit} validate={validate} subscription={{ submitting: true }}>
      {({ handleSubmit, submitting }) => {
        return (
          <SecurityDialog
            open={open}
            onCancel={onCancel}
            disableSubmit={submitting}
            onConfirm={handleSubmit}
          >
            <form onSubmit={(e: FormEvent<HTMLFormElement>) => handleSubmit(e)}>
              <h4 className={s.title}>Enter the code we sent you</h4>
              <div>
                <Field
                  component={TextField}
                  changeOnBlur={false}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  name="otp"
                  label="Verification Code"
                  variant="outlined"
                  classes={{ root: s.verificationCode }}
                  inputType="number"
                />
                <Button
                  variant="text"
                  size="medium"
                  classes={{ root: s.resend }}
                  onClick={handleResend}
                >
                  Resend
                </Button>
              </div>
              {method === OTP_METHODS.EMAIL && <EmailDeliveryHelp align="start" />}
            </form>
          </SecurityDialog>
        );
      }}
    </Form>
  );
};

const VerificationStep = verificationStepConnector(VerificationStepBare);

const methodSelectionConnector = connect(
  (state: RootState) => ({
    viewer: state.viewer,
    // @ts-expect-error Property 'otpAuthUri' does not exist on type '{}'.
    otpAuthUri: state.auth.otpAuthUri,
  }),
  {
    notify,
    fetchUser,
    authenticateOtp,
    otpQrCodeBase64,
    createOtpAuthConfiguration,
  }
);

interface MethodSelectionProps {
  user?: User;
  email: string;
  method: string;
  setMethod: (method: OtpMethod) => void;
  onSuccess: (address: string) => void;
  handleVerificationSuccess: () => void;
  setOtp: (otp: string) => void;
  open: boolean;
  onCancel: () => void;
}

interface MethodFormData {
  email: string;
  otp: string;
  phone: string;
  method_state: OtpMethod;
}

type MethodFormErrors = Partial<Record<keyof MethodFormData, string>>;

function validateMethod(values: MethodFormData): MethodFormErrors {
  const errors: MethodFormErrors = {};
  if (values.method_state === OTP_METHODS.EMAIL && !(values.email || '').trim()) {
    errors.email = 'Required';
  }
  if (values.method_state === OTP_METHODS.SMS && !(values.phone || '').trim()) {
    errors.phone = 'Required';
  }
  if (values.method_state === OTP_METHODS.APP && !(values.otp || '').trim()) {
    errors.otp = 'Required';
  }

  return errors;
}

const MethodSelectionBare: FC<
  MethodSelectionProps & ConnectedProps<typeof methodSelectionConnector>
> = ({
  user,
  email,
  viewer,
  method,
  setMethod,
  createOtpAuthConfiguration,
  otpAuthUri,
  authenticateOtp,
  otpQrCodeBase64,
  fetchUser,
  onSuccess,
  handleVerificationSuccess,
  notify,
  setOtp,
  open,
  onCancel,
}) => {
  const s = useStyles();

  const [showFAQ, setShowFAQ] = useState(true);
  const [qrCodeBase64, setQRCodeBase64] = useState<string | undefined>(undefined);

  const handleSubmit = useCallback(
    async (values: MethodFormData) => {
      try {
        switch (values.method_state) {
          case 'email':
            await createOtpAuthConfiguration(values.method_state, values.email);
            onSuccess(values.email);
            break;
          case 'app': {
            setShowFAQ(false);
            const success = await authenticateOtp(values.otp);
            if (!success) {
              setShowFAQ(true);
              return { otp: 'Invalid verification code' };
            }
            if (user) {
              await fetchUser(user.username, {
                force: true,
                otpAuthEnabled: true,
              });
            } else {
              setOtp(values.otp);
            }
            handleVerificationSuccess();
            break;
          }
          default:
            await createOtpAuthConfiguration(values.method_state, values.phone);
            onSuccess(values.phone);
        }
      } catch (err) {
        if (err instanceof APIError) {
          notify(apiErrorMessage(err, SETUP_ERROR), 'error');
        } else {
          notify(SETUP_ERROR, 'error');
          throw err;
        }
      }
    },
    [
      authenticateOtp,
      createOtpAuthConfiguration,
      fetchUser,
      handleVerificationSuccess,
      notify,
      onSuccess,
      setOtp,
      user,
    ]
  );

  const handleChange = useCallback(
    async (value: OtpMethod) => {
      try {
        await setMethod(value);
        if (value === OTP_METHODS.APP) {
          await createOtpAuthConfiguration(OTP_METHODS.APP, '');
          const res = await otpQrCodeBase64();
          setQRCodeBase64(res);
        }
      } catch (err) {
        // Same as handleResend: the radio group's onChange drops the promise.
        Sentry.captureException(err);
        notify(
          apiErrorMessage(
            err,
            'An error occurred when trying to select the two-factor authenticator method.'
          ),
          'error'
        );
      }
    },
    [createOtpAuthConfiguration, notify, otpQrCodeBase64, setMethod]
  );

  const initialValues = useMemo(() => ({ phone: viewer.phone }), [viewer.phone]);

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validateMethod}
      subscription={{ submitting: true }}
      initialValues={initialValues}
    >
      {({ handleSubmit, submitting }) => {
        return (
          <SecurityDialog
            open={open}
            onCancel={onCancel}
            disableSubmit={submitting}
            onConfirm={handleSubmit}
          >
            <form onSubmit={(e: FormEvent<HTMLFormElement>) => handleSubmit(e)}>
              <h4 className={s.title}>Set up two-factor authentication</h4>
              <Field
                name="method_state"
                component={RadioGroup}
                onChange={handleChange}
                label="Select the authentication method"
                FormControlProps={{ style: { marginTop: 20 } }}
                style={{ flexDirection: 'row', justifyContent: 'center' }}
                defaultValue={method}
                options={[
                  {
                    label: capitalize(OTP_METHODS.EMAIL),
                    value: OTP_METHODS.EMAIL,
                  },
                  // {
                  //   label: capitalize(otpTypes.sms),
                  //   value: otpTypes.sms,
                  // },
                ]}
              />
              {method === OTP_METHODS.SMS && (
                <Field
                  component={PhoneInput}
                  name="phone"
                  label="Phone"
                  variant="outlined"
                  showExampleOnError
                />
              )}
              {method === OTP_METHODS.EMAIL && (
                <Field
                  name="email"
                  component={TextField}
                  defaultValue={email}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  label="Email"
                  variant="outlined"
                  disabled={!user}
                />
              )}
              {method === OTP_METHODS.APP && qrCodeBase64 && (
                <AuthenticatorStep
                  otpAuthUri={otpAuthUri}
                  qrCodeBase64={qrCodeBase64}
                  showFAQ={showFAQ}
                />
              )}
            </form>
          </SecurityDialog>
        );
      }}
    </Form>
  );
};

const MethodSelection = methodSelectionConnector(MethodSelectionBare);

interface EnableOtpAuthProps {
  open: boolean;
  onCancel: () => void;
  user?: User;
  email: string;
  setOtp: (otp: string) => void;
  // TODO: @jboscan-onfrontiers to remove this
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch?: () => Promise<any>;
}

export const EnableOtpAuth: FC<EnableOtpAuthProps> = ({
  user,
  email,
  setOtp,
  refetch,
  open,
  onCancel,
}) => {
  const initialStep = 'select';
  const initialMethod = OTP_METHODS.EMAIL;

  const [step, setStep] = useState(initialStep);
  const [method, setMethod] = useState<string>(initialMethod);
  const [address, setAddress] = useState('');

  // const handleExit = useCallback(() => {
  //   setStep(initialStep);
  //   setMethod(initialMethod);
  // }, [initialMethod]);

  const handleSelectionSuccess = useCallback((address: string) => {
    setStep('verification');
    setAddress(address);
  }, []);

  const handleVerificationSuccess = useCallback(() => setStep('success'), []);

  return (
    <>
      {step === 'select' && (
        <MethodSelection
          open={open}
          onCancel={onCancel}
          //onExited={handleExit}
          onSuccess={handleSelectionSuccess}
          handleVerificationSuccess={handleVerificationSuccess}
          method={method}
          email={email}
          user={user}
          setMethod={setMethod}
          setOtp={setOtp}
        />
      )}
      {step === 'verification' && (
        <VerificationStep
          open={open}
          onCancel={onCancel}
          address={address}
          method={method}
          user={user}
          //onExited={handleExit}
          onSuccess={handleVerificationSuccess}
          setOtp={setOtp}
          refetch={refetch}
        />
      )}
      {step === 'success' && <SuccessStep user={user} />}
    </>
  );
};

export default EnableOtpAuth;
